import { useAtom, useSetAtom } from "jotai";
import { useCallback, useEffect, useEffectEvent, useRef } from "react";
import { useLocation } from "react-router";

import { isUserInput } from "@skilitics/designer-ui-components";

import { useContextNodeId } from "../navigation/index.ts";
import { useResetAspect, useSetSelectedNodeId } from "../selection/index.ts";
import { pendingSelectionAtom, selectedAspectAtom } from "../selection/selectionAtoms.ts";

export const useSelectedNodeResetter = (rootNodeId?: number) => {
  const setSelectedNodeId = useSetSelectedNodeId();
  const setSelectedAspect = useSetAtom(selectedAspectAtom);
  const resetAspect = useResetAspect();
  const contextNodeId = useContextNodeId();
  const { pathname } = useLocation();
  const [pendingSelection, setPendingSelection] = useAtom(pendingSelectionAtom);

  // The route a jump's selection stands on, so the context node arriving a render or two after the
  // pathname doesn't reset the selection out from under it.
  const jumpedToPathname = useRef<string | undefined>(undefined);

  const resetSelectedNodeId = useCallback(() => {
    jumpedToPathname.current = undefined;
    setSelectedNodeId(contextNodeId ?? rootNodeId);
    // Route change / Escape is a full reset, so drop back to the default aspect too.
    resetAspect();
  }, [contextNodeId, rootNodeId, setSelectedNodeId, resetAspect]);

  const applyRouteSelection = useEffectEvent(() => {
    if (pendingSelection) {
      // Held until its route is reached. Any other route drops it, so a request the router never
      // served can't hijack a later navigation.
      if (pendingSelection.path === pathname) {
        setSelectedNodeId(pendingSelection.nodeId);
        setSelectedAspect(pendingSelection.aspect);
        // Spent: from here the route keeps the selection, rather than the request re-applying it
        // over whatever the author picks next.
        setPendingSelection(undefined);
        jumpedToPathname.current = pathname;
        return;
      }
      setPendingSelection(undefined);
    }
    if (jumpedToPathname.current === pathname) return;
    resetSelectedNodeId();
  });

  useEffect(() => {
    // Reset node selection when the route changes, unless a jump asked for a node there.
    applyRouteSelection();
  }, [pathname, contextNodeId, rootNodeId]);

  const handleKeyDown = useCallback(
    (e: globalThis.KeyboardEvent) => {
      if (isUserInput(e.target)) return;

      if (e.key === "Escape") {
        resetSelectedNodeId();
      }
    },
    [resetSelectedNodeId],
  );

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleKeyDown]);
};
